/**
 * The droom prompt channel (S13-9 slice 1) — officer-initiated guided captures
 * during the live call. Wire contract (vCIP_be SessionPromptController):
 *
 *   GET  /api/v1/sessions/{id}/prompts:pending           → { prompt: CapturePrompt | null }
 *        The single in-flight prompt, or an honest null. The be applies lazy
 *        expiry first — a dead prompt is never served, so the customer is
 *        never asked to complete something already expired/cancelled.
 *   POST /api/v1/sessions/{id}/prompts/{promptId}:ack    → CapturePrompt
 *        Ack-on-show: the customer has SEEN who asked and what for, BEFORE the
 *        capture UI does anything (REG transparency floor). Idempotent.
 *   POST /api/v1/sessions/{id}/prompts/{promptId}:capture → multipart `file`
 *        201 { prompt, artifact } — the frame lands at origin LIVE_CAPTURE
 *        (E2), linked server-side to the officer's invocation. 409 = the
 *        prompt went terminal meanwhile (cancelled/expired/already captured) —
 *        treat as "request withdrawn", return to the call calmly.
 *
 * Transparency honesty: the wire carries the officer's reason CATEGORY (a
 * closed vocabulary the overlay renders in customer language) — NEVER the
 * officer's verbatim reason. What the customer sees is who asked (their
 * verifier) and what for (the category copy); nothing is captured silently.
 *
 * Mock mode (no backend): no prompt channel exists — pending resolves null and
 * the mutations no-op honestly (nothing pretends an officer asked).
 */

import { delay, isMockMode, request } from './client';

export type PromptStateWire =
  | 'PENDING'
  | 'ACKNOWLEDGED'
  | 'COMPLETED'
  | 'EXPIRED'
  | 'CANCELLED';

export type PromptReasonCategory =
  | 'DOCUMENT_UNCLEAR'
  | 'DOCUMENT_MISMATCH'
  | 'TAMPER_SUSPECTED'
  | 'IDENTITY_DOUBT'
  | 'FLOW_STEP'
  | 'REGULATORY_REQUIREMENT'
  | 'OTHER';

/** The prompted primitive keys this droom build knows how to render. */
export type PromptPrimitiveKey = 'doc-show-hold' | 'selfie-while-holding' | 'selfie-live';

export interface CapturePrompt {
  id: string;
  sessionId: string;
  /** Server key — render via the known-key map; an UNKNOWN key is skipped honestly (deploy skew). */
  primitiveKey: string;
  label: string;
  reasonCategory: PromptReasonCategory | string;
  state: PromptStateWire;
  createdAt: string;
  expiresAt: string;
  acknowledgedAt: string | null;
  invocationId: string;
  /**
   * CAM-1: the officer's camera preference for this capture — 'ENVIRONMENT' (rear) / 'USER' (front),
   * or null/absent when NO preference was expressed. Absence is meaningful: it means the officer said
   * nothing, not "use the front camera". We only show the customer a switch notice when a lens was
   * actually requested, so defaulting this would prompt them about a choice nobody made.
   *
   * Optional on the wire so a droom running ahead of the backend degrades to "no preference" rather
   * than breaking the prompt channel (the deploy-skew posture the primitiveKey map already takes).
   */
  requestedFacing?: 'ENVIRONMENT' | 'USER' | null;
  /**
   * SPEC-13 D4 (additive this release): the prompt now NAMES the document.
   * `docType` is the registry key (PAN_CARD, PASSPORT, …); `docTypeLabel` is
   * the registry's display label ("Passport — photo page"); `captureGuidance`
   * is the per-type guidance line; `guideAspect` is the registry guide-frame
   * aspect (LANDSCAPE/PORTRAIT) driving the orientation advisory. ALL optional
   * on the wire: an older be serves none of them and the overlay renders
   * today's generic prompt — absence is never guessed into a document name.
   */
  docType?: string | null;
  docTypeLabel?: string | null;
  captureGuidance?: string | null;
  guideAspect?: string | number | null;
  /**
   * V116 / owner item 4 — the server's record of the customer having acknowledged the purpose notice
   * and the camera being live. Reported by THIS client via {@link reportReady}; echoed back so a
   * droom that reloads mid-prompt knows it has already reported, and so the server's view is what
   * governs rather than a local flag.
   */
  readyAt?: string | null;
  /**
   * V116 / owner item 4 — when the officer asked for the shutter to fire. The FALLBACK path: the
   * LiveKit data relay normally arrives first, but if it did not (relay hiccup, mid-reconnect, media
   * disabled) this field on the ordinary poll still carries the request. Absent = never asked.
   */
  shutterRequestedAt?: string | null;
  /** V116 — who pressed for the frame that landed. Absent on pre-V116 prompts (never backfilled). */
  shutterActor?: 'CUSTOMER' | 'OFFICER' | null;
}

/** Metadata echo of the admitted live-capture artifact (never the bytes). */
export interface PromptCaptureArtifact {
  kind: string;
  origin: string | null;
  evidenceGrade: string | null;
  contentType: string;
  sha256: string;
  capturedAt: string;
}

export interface PromptCaptureResult {
  prompt: CapturePrompt;
  artifact: PromptCaptureArtifact;
}

/** The session's in-flight prompt, or null (honest none / mock mode). */
export async function fetchPendingPrompt(sessionId: string): Promise<CapturePrompt | null> {
  if (isMockMode()) {
    await delay(120);
    return null; // no prompt channel exists — never fabricate an officer request
  }
  const res = await request<{ prompt: CapturePrompt | null }>(
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/prompts:pending`,
  );
  return res.prompt ?? null;
}

/**
 * Ack-on-show (idempotent). Failures are non-blocking — the overlay still renders.
 *
 * C10: carries the just-in-time disclosure THIS customer was shown — version, digest-as-rendered and
 * locale — so the server can stamp it on the prompt and an examiner can reconstruct what was read,
 * in which language, instead of inferring it from today's copy. Every field is optional on the wire:
 * an older backend ignores the body, and a browser without WebCrypto sends no digest rather than a
 * fabricated one.
 */
export async function ackPrompt(
  sessionId: string,
  promptId: string,
  disclosure?: {
    readonly disclosureVersion: string;
    readonly disclosureSha256: string | null;
    readonly disclosureLocale: string;
  },
): Promise<CapturePrompt | null> {
  if (isMockMode()) return null;
  return request<CapturePrompt>(
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/prompts/${encodeURIComponent(promptId)}:ack`,
    { method: 'POST', ...(disclosure ? { body: disclosure } : {}) },
  );
}

/**
 * Report that the capture surface is READY (V116 / owner item 4): the customer has acknowledged the
 * purpose notice as their own affirmative act, and the published camera track is live.
 *
 * <p>Distinct from {@link ackPrompt} on purpose. Ack-on-show says the overlay was DISPLAYED; this says
 * the customer ANSWERED it. The server refuses the officer's remote shutter until this lands, so the
 * two must never be collapsed — that would automate exactly the acknowledgement DPDP s.6 requires be
 * made by the person.
 *
 * <p>Best-effort like the ack: a failure here costs the officer the remote shutter (they ask the
 * customer to press it, as before), never the capture itself.
 */
export async function reportReady(sessionId: string, promptId: string): Promise<void> {
  if (isMockMode()) return;
  await request<CapturePrompt>(
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/prompts/${encodeURIComponent(promptId)}:ready`,
    { method: 'POST' },
  );
}

/** Who pressed the shutter for the frame being uploaded (V116 — an evidential fact, never guessed). */
export type ShutterActor = 'CUSTOMER' | 'OFFICER';

/**
 * The capture URL, exported so the CAP-4 wire test asserts THE function that ships — not a copy.
 * (Review finding 2026-08-10: the original test reimplemented this builder locally, so the real
 * one could drift with the test still green — the exact failure class the test exists to catch.)
 */
export function promptCaptureUrl(
  sessionId: string,
  promptId: string,
  discardedReasons: readonly string[],
  /** V116 — who pressed. Omitted when unknown: the server records nothing rather than assuming. */
  shutterActor?: ShutterActor,
): string {
  // Repeated query params — how Spring binds a List<String> @RequestParam. Sent on the query string
  // rather than as form fields so the multipart body stays exactly one part: the frame.
  const params = discardedReasons.map((r) => `discardedReasons=${encodeURIComponent(r)}`);
  if (shutterActor) params.push(`shutterActor=${encodeURIComponent(shutterActor)}`);
  const qs = params.join('&');
  return (
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/prompts/${encodeURIComponent(promptId)}:capture`
    + (qs ? `?${qs}` : '')
  );
}

/**
 * Owner item 9 — THE IN-CALL UPLOAD FALLBACK URL.
 *
 * A route of its own, NOT a flag on `:capture`. The server stamps evidence provenance from the
 * endpoint that was used (INTAKE-01 I2), so a `source=UPLOAD` parameter would be this client
 * asserting its own provenance — exactly what that rule forbids. Two doors, two origins, and no
 * field for a client to lie in: a file sent here can only ever become UPLOAD/E4.
 */
export function promptUploadUrl(
  sessionId: string,
  promptId: string,
  discardedReasons: readonly string[],
): string {
  const params = discardedReasons.map((r) => `discardedReasons=${encodeURIComponent(r)}`);
  const qs = params.join('&');
  return (
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/prompts/${encodeURIComponent(promptId)}:upload`
    + (qs ? `?${qs}` : '')
  );
}

/**
 * Send a FILE as the answer to this prompt, because the camera would not produce a usable frame.
 *
 * Completes the prompt server-side exactly as a capture does — the officer's worklist advances
 * either way — but the artefact is stamped UPLOAD/E4 rather than the E2 of a live frame. The
 * discarded capture attempts ride along, so the record shows WHY the fallback was used.
 */
export async function uploadPromptDocument(
  sessionId: string,
  promptId: string,
  file: Blob,
  fileName: string,
  discardedReasons: readonly string[] = [],
): Promise<PromptCaptureResult> {
  if (isMockMode()) {
    throw new Error('mock mode has no prompt channel');
  }
  const form = new FormData();
  form.append('file', file, fileName);
  return request<PromptCaptureResult>(
    promptUploadUrl(sessionId, promptId, discardedReasons),
    { method: 'POST', body: form },
  );
}

/** Upload the guided capture — completes the prompt server-side (one frame per prompt). */
export async function uploadPromptCapture(
  sessionId: string,
  promptId: string,
  blob: Blob,
  /**
   * CAP-4: the on-device quality reason for each shot the customer DISCARDED before this one, oldest
   * first. Rides the accepted upload rather than a route of its own — a dedicated droom endpoint would
   * be scope-only (the customer holds scopes, not roles) and AuthzBuildGateTest rightly refuses those.
   *
   * The BYTES of a discard are never sent: the examiner needs the count and the reasons, not a
   * multiplied store of biometric data for every fumbled photo.
   */
  discardedReasons: readonly string[] = [],
  /**
   * V116 / item 4 — who pressed the shutter for THIS frame. Provenance, not telemetry: an examiner
   * reading the capture must be able to tell whether the customer took it or the verifier did.
   */
  shutterActor?: ShutterActor,
): Promise<PromptCaptureResult> {
  if (isMockMode()) {
    throw new Error('mock mode has no prompt channel'); // unreachable: pending is always null in mock
  }
  const form = new FormData();
  form.append('file', blob, 'guided-capture.jpg');
  return request<PromptCaptureResult>(
    promptCaptureUrl(sessionId, promptId, discardedReasons, shutterActor),
    { method: 'POST', body: form },
  );
}
